import React from "react";
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  type SelectChangeEvent,
  Stack,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import {
  extractSpiddBoardName,
  findBoardForSpiddLabel,
  tableauDisplayTitle,
} from "./displayHelpers";
import { accentColor, isDarkMode, surfaceSx } from "./resultatsTheme";
import type { ResultsDoc } from "./types";

interface BoardRef {
  name: string;
  label: string;
}

interface TableauSelectorProps {
  docs: ResultsDoc[];
  boards?: BoardRef[];
  value: string;
  onChange: (tableauId: string) => void;
}

function optionLabel(doc: ResultsDoc, boards: BoardRef[]): string {
  const board = findBoardForSpiddLabel(boards, doc.label);
  const title = tableauDisplayTitle(doc, boards);
  const code = board?.name.trim() || extractSpiddBoardName(doc.label);
  if (code && title !== code) return `${code} — ${title}`;
  return title;
}

export default function TableauSelector({
  docs,
  boards = [],
  value,
  onChange,
}: TableauSelectorProps): React.ReactElement | null {
  const theme = useTheme();
  const isDark = isDarkMode(theme);
  const accent = accentColor(theme);
  const idleBorder = isDark
    ? alpha(theme.palette.common.white, 0.42)
    : alpha(theme.palette.primary.main, 0.35);
  const idleLabel = isDark
    ? alpha(theme.palette.common.white, 0.78)
    : theme.palette.text.secondary;

  if (docs.length <= 1) return null;

  const selected = docs.find((d) => d.sourceTableauId === value) ?? docs[0];
  const selectedTitle = selected ? optionLabel(selected, boards) : "";

  return (
    <Stack
      spacing={1}
      sx={{
        ...surfaceSx(theme),
        p: { xs: 1.5, sm: 2 },
        mb: { xs: 2, md: 2.5 },
      }}
    >
      <Typography
        variant="overline"
        sx={{
          letterSpacing: "0.12em",
          fontWeight: 700,
          color: accent,
          lineHeight: 1.2,
        }}
      >
        Choisir un tableau
      </Typography>
      <FormControl
        fullWidth
        size="small"
        sx={{
          bgcolor: isDark
            ? alpha(theme.palette.common.white, 0.04)
            : alpha(theme.palette.common.white, 0.7),
          borderRadius: 2.5,
          "& .MuiInputLabel-root": {
            color: idleLabel,
            "&.Mui-focused": { color: accent },
          },
          "& .MuiOutlinedInput-root": {
            borderRadius: 2.5,
            color: theme.palette.text.primary,
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: idleBorder,
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: accent,
            },
            "&.Mui-focused": {
              boxShadow: `0 0 0 3px ${alpha(accent, isDark ? 0.28 : 0.18)}`,
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: accent,
                borderWidth: 1.5,
              },
            },
            "& .MuiSelect-icon": {
              color: idleLabel,
            },
          },
        }}
      >
        <InputLabel id="resultats-tableau-label">Tableau</InputLabel>
        <Select
          labelId="resultats-tableau-label"
          id="resultats-tableau"
          value={value}
          label="Tableau"
          onChange={(event: SelectChangeEvent) => {
            onChange(event.target.value);
          }}
          renderValue={() => selectedTitle}
        >
          {docs.map((doc) => (
            <MenuItem key={doc.id} value={doc.sourceTableauId}>
              {optionLabel(doc, boards)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Stack>
  );
}
